import React, {MutableRefObject,  useContext,  useEffect, useState, useRef} from 'react';
import {useTypedFastStorage} from '../../hooks/useFastStorage';
import SecondaryButton from "../../ui/SecondaryButton";
import {ApplicableTableSettings} from "../../tableSettings";
import SmartTable, { SmartTableHandle } from "../../uiControls/SmartTable";
// import {historyViewPaymentMainDetailTable, historyViewPaymentBillingDetailTable, historyViewPaymentRevisionTable} from "./tableSettings";
import InlineInputControlRow from '../../ui/layout/InlineInputControlRow';
import TextBox from '../../ui/TextBox';
import IfmsDate from '../../ui/Date';
import Float from '../../ui/Float';
import ApplicationContext from '../../ApplicationContext';
import useGlobalOkDialog from '../../hooks/useGlobalOkDialog';
import { ApiResult } from '../../framework.core/data/ApiResult';
import { DateRenderer, DateTimeRenderer, FloatRenderer, NullableCheckboxIconRenderer, NumberedIndicatorRenderer, TextAlignLeftRenderer, TextRenderer } from '../../uiControls/SmartTableRenderers';
import useTableNavigation from '../../ui/tables/useTableNavigation';
import { PaymentHistViewModel } from '../../fms.core/PaymentHistViewModel';
import { FmsSubMenu } from '../../constants/fmsSubMenu';
import { historyViewPaymentDetailsTable, historyViewPaymentMainTable } from './tableSettings';
import RadioButton from '../../ui/RadioButton';

interface HistoryViewPaymentStorageKeys {
  modelStorage: string,
};

interface HistoryViewPaymentParams {
  onPopupClose?: () => void,
  jobId: number,
  jobType: string,
};

interface HistoryViewPaymentLoadedParams {
  historyViewPayment: PaymentHistViewModel,
  historyViewPaymentRef: MutableRefObject<PaymentHistViewModel | null>,
  setHistoryViewPayment: (x: Partial<PaymentHistViewModel>) => void,
  jobType: string,
  onPopupClose?: () => void,
};

function getHistoryViewPaymentStorageKeys(): HistoryViewPaymentStorageKeys {
  return {modelStorage: 'common-history-payment-view'}
}

function HistoryViewPaymentImpl(params: HistoryViewPaymentParams) {
  const appContext = useContext(ApplicationContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();

  const {modelStorage} = getHistoryViewPaymentStorageKeys();
  const {jobId, jobType, onPopupClose} = params;

  const [inStorage, setInStorage, removeFromStorage, storageRef] = useTypedFastStorage<PaymentHistViewModel>(modelStorage, null);

  useEffect(() => {
      initialize();
      return () => removeFromStorage()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobId]
  );

  async function loadExisting(): Promise<PaymentHistViewModel|null> {
    const apiResult:ApiResult<PaymentHistViewModel> = await appContext.api.getPaymentHistory(jobId, jobType);
    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(apiResult?.message || 'Cannot load data');
      if (onPopupClose) {
        onPopupClose();
      }
    }

    return apiResult.result;
  }

  async function initialize() {
    const data = await loadExisting();
    if (data) {
      setInStorage({...data});
    }
  }

  if (!inStorage) {
    return null;
  }

  return (
    <HistoryViewPaymentInitializedImpl
      historyViewPayment={inStorage}
      historyViewPaymentRef={storageRef}
      setHistoryViewPayment={setInStorage}
      onPopupClose={params.onPopupClose}
      jobType={jobType}
    />
  );
}

function HistoryViewPaymentInitializedImpl({
  historyViewPayment,
  historyViewPaymentRef,
  setHistoryViewPayment,
  jobType,
  onPopupClose,
}: HistoryViewPaymentLoadedParams) {
  const [selectedRow, setSelectedRow] = useState(historyViewPayment.detailList.length-1);
  const selectPaymentHist = historyViewPayment.histDetailList.filter(x => x.hdId === historyViewPayment.detailList[selectedRow].id);

  historyViewPayment.tempHistDetail = [];
  historyViewPayment.histDetailList.forEach(x=> {
    if (x.id === selectPaymentHist[0]?.id) {
      historyViewPayment.tempHistDetail.push(x);
    }
  });

  useTableNavigation('historyViewPaymentMainTable', setSelectedRow, setSelectedRow, [], false, true);

  const masterTableRef = useRef<SmartTableHandle>(null);

  function onMasterTableInitialized() {
    if (masterTableRef.current && jobType === FmsSubMenu.MPHistory) {
      masterTableRef.current.showColumn('custName', false, false);
      setTimeout(() => {
        if (masterTableRef.current) {
          masterTableRef.current.showColumn('draft', false, false);
          masterTableRef.current.resizeColumn('block', 40, false);
        }
      }, 20);
    }

    const focusElement = document.getElementById(`historyViewPaymentMainTable-row-${selectedRow}`) as HTMLInputElement ;
    if(focusElement){
      focusElement.scrollIntoView({behavior: 'smooth', block: 'end'});
    }
  }

  function getCustomerLabel(): string {
    if (jobType === FmsSubMenu.CPHistory) {
      return 'Customer';
    }
    if (jobType === FmsSubMenu.BPHistory) {
      return 'Vendor';
    }
    if (jobType === FmsSubMenu.MPHistory) {
      return 'Vendor';
    }

    return '';
  }

  function getTotalLabel(): string {
    if (jobType === FmsSubMenu.CPHistory) {
      return 'Deposit Total';
    }
    if (jobType === FmsSubMenu.BPHistory) {
      return 'Payment Total';
    }
    if (jobType === FmsSubMenu.MPHistory) {
      return 'Payment Total';
    }

    return '';
  }

  function getAmountLabel(): string {
    if (jobType === FmsSubMenu.CPHistory) {
      return 'Received Total';
    }
    if (jobType === FmsSubMenu.BPHistory) {
      return 'Paid Total';
    }
    if (jobType === FmsSubMenu.MPHistory) {
      return 'Paid Total';
    }

    return '';
  }

  function getEntityLabel(): string {
    if (jobType === FmsSubMenu.CPHistory) {
      return 'Bank Entity';
    }
    if (jobType === FmsSubMenu.BPHistory) {
      return 'Bank Entity';
    }
    if (jobType === FmsSubMenu.MPHistory) {
      return 'Credit Card';
    }

    return '';
  }

  function getReceivedFromLabel(): string {
    if (jobType === FmsSubMenu.CPHistory) {
      return 'Received From';
    }
    if (jobType === FmsSubMenu.BPHistory) {
      return 'Pay To';
    }
    if (jobType === FmsSubMenu.MPHistory) {
      return ''; // Not visible
    }

    return '';
  }

  function getRefNoLabel(): string {
    if (jobType === FmsSubMenu.CPHistory || jobType === FmsSubMenu.BPHistory) {
      if (historyViewPayment.etf === "1") {
        return 'W/T #';
      }
      else if (historyViewPayment.etf === "3") {
        return 'ACH #';
      }
    }

    return 'Check #';
  }

  const mainTableOptions: ApplicableTableSettings = {
    ...historyViewPaymentMainTable,
    selectable: true,
    rowProps: (row: any) => ({onClick: () => setSelectedRow(row.index)}),
    selectedRowIndex: selectedRow,
    customTableId: 'historyViewPaymentMainTable',
    tableName: 'historyViewPaymentMainTable',
    data: historyViewPayment.detailList,
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      { accessor: 'historyDate', Cell: DateTimeRenderer },
      { accessor: 'historyUser', Cell: TextAlignLeftRenderer },
      { accessor: 'historyAction', Cell: TextRenderer },
      { accessor: 'cardBank', Cell: TextAlignLeftRenderer, Header: () => <span>{getEntityLabel()}</span> },
      { accessor: 'postDate', Cell: DateRenderer },
      { accessor: 'checkNo', Cell: TextAlignLeftRenderer, Header: () => <span>{getRefNoLabel()}</span> },
      { accessor: 'currency', Cell: TextRenderer },
      { accessor: 'exRate', Cell: (props: any) => <FloatRenderer {...props} decimals={4} /> },
      { accessor: 'block', Cell: NullableCheckboxIconRenderer },
      { accessor: 'draft', Cell: NullableCheckboxIconRenderer },
      { accessor: 'custName', Cell: TextAlignLeftRenderer, Header: () => <span>{getReceivedFromLabel()}</span> },
      { accessor: 'remark', Cell: TextAlignLeftRenderer },
      { accessor: 'totalAmount', Cell: FloatRenderer, Header: () => <span>{getTotalLabel()}</span> },
    ],
  };

  const paymentDetailTableOptions: ApplicableTableSettings = {
    ...historyViewPaymentDetailsTable,
    selectable: true,
    tableName: 'historyViewPaymentDetailsTable',
    // data: selectedRow >= 0 ? historyViewPayment.histDetailList.filter(x => x.hdId === historyViewPayment.detailList[selectedRow].id) : [],
    data: selectedRow >= 0 ? historyViewPayment.tempHistDetail : [],
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      {accessor: 'status', Cell: TextAlignLeftRenderer },
      {accessor: 'customer', Cell: TextAlignLeftRenderer, Header: () => <span>{getCustomerLabel()}</span> },
      {accessor: 'invoiceDate', Cell: DateRenderer },
      {accessor: 'invoiceNo', Cell: TextAlignLeftRenderer },
      {accessor: 'accountCode', Cell: TextRenderer },
      {accessor: 'description', Cell:TextAlignLeftRenderer },
      {accessor: 'amount', Cell: FloatRenderer, Header: () => <span>{getAmountLabel()}</span> },
    ],
    rowProps: (row:any) => {
      if (historyViewPayment.tempHistDetail[row.index].status === "Original") {
        return {
          style: {
            color: 'red'
          }
        }
      }
    }
  };

  return (
    <div className='report-popup'>
      <div style={{display: 'flex'}}>
        <div style={{width: '280px'}}>
          <InlineInputControlRow label={getCustomerLabel()} mode="w100">
            <TextBox disabled value={historyViewPayment.entity} />
          </InlineInputControlRow>
        </div>

        <div style={{marginLeft: '40px', width: '300px'}}>
          <InlineInputControlRow label={jobType !== FmsSubMenu.MPHistory ? 'Payment Type' : 'CC Ref #'} mode="w100">
            {jobType !== FmsSubMenu.MPHistory
              ?
                <div style={{display: 'flex'}}>
                  <div style={{width: '80px'}}>
                    <RadioButton disabled group="payment-type" label="Check" reverse checked={historyViewPayment.etf === "0"} />
                  </div>
                  <div style={{marginLeft: '20px', width: '80px'}}>
                    <RadioButton disabled group="payment-type" label="ACH" reverse checked={historyViewPayment.etf === "3"} />
                  </div>
                  <div style={{marginLeft: '20px', width: '80px'}}>
                    <RadioButton disabled group="payment-type" label="W/T" reverse checked={historyViewPayment.etf === "1"} />
                  </div>
                </div>
              :
                <TextBox disabled value={historyViewPayment.ccRefNo} />
            }
          </InlineInputControlRow>
        </div>

        <div style={{marginLeft: '40px', width: '160px'}}>
          <InlineInputControlRow label="Accrual Date" mode="w80">
            <IfmsDate disabled value={historyViewPayment.postDate} />
          </InlineInputControlRow>
        </div>
      </div>

      <div style={{display: 'flex'}}>
        <div style={{width: '280px'}}>
          <InlineInputControlRow label="Bank Entity" mode="w100">
            <TextBox disabled value={historyViewPayment.bankName} />
          </InlineInputControlRow>
        </div>

        <div style={{marginLeft: '40px', width: '300px'}}>
          <InlineInputControlRow label={getRefNoLabel()} mode="w100">
            <div style={{width: '80px'}}>
              <TextBox disabled value={historyViewPayment.checkNo} />
            </div>
          </InlineInputControlRow>
        </div>

        <div style={{marginLeft: '40px', width: '160px'}}>
          <InlineInputControlRow label={getTotalLabel()} mode="w80">
            <Float disabled value={historyViewPayment.total} />
          </InlineInputControlRow>
        </div>

        {jobType === FmsSubMenu.MPHistory &&
          <div style={{marginLeft: '40px', width: '280px'}}>
            <InlineInputControlRow label="Credit Card" mode="w100">
              <TextBox disabled value={historyViewPayment.creditCard} />
            </InlineInputControlRow>
          </div>
        }
      </div>

      <SmartTable ref={masterTableRef} {...mainTableOptions} onInitialized={onMasterTableInitialized} />

      <div style={{display: 'flex'}}>
        {/* <div style={{margin: 'auto'}}></div> */}
        <div style={{width: '190px', textAlign: 'right', paddingRight: '10px'}}>
          Payment Detail
        </div>
        <div style={{width: '860px'}}>
          <SmartTable {...paymentDetailTableOptions} />
        </div>

      </div>

      <div className='report-footer'>
        <SecondaryButton
          size="regular"
          style={{width: '90px'}}
          onClick={onPopupClose}>
          CLOSE
        </SecondaryButton>
      </div>
    </div>
  );
}

const HistoryViewPayment = ({
  onPopupClose,
  jobId,
  jobType,
}: { onPopupClose?: () => void, jobId: number, jobType: string }) => {
  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
   */

  return (
    <HistoryViewPaymentImpl jobType={jobType} jobId={jobId} onPopupClose={onPopupClose} key={`historyViewPayment-details`}/>
  );
};
export default HistoryViewPayment;
